import { eachDayOfInterval, endOfMonth, endOfWeek, isSameDay, isSameMonth, isWeekend, parseISO, startOfMonth, startOfWeek } from "date-fns";

import type { Absence } from "~/modules/absence/api/absence/absenceTypes.ts";
import type { AbsenceType } from "~/modules/absence/api/absenceType/absenceTypeTypes.ts";
import type { Holiday } from "~/modules/absence/api/holiday/holidayTypes.ts";
import type { TimeTrackingExtendedType } from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import { getAbsencesOnDay } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";

export type CalendarDay = {
	date: Date;
	isInMonth: boolean;
	isToday: boolean;
	isWeekend: boolean;
	timeTrackings: TimeTrackingExtendedType[];
	holiday?: Holiday;
	absences: ReturnType<typeof getAbsencesOnDay>;
};

/** The first and the last day that the grid of a month (`YYYY-MM`) shows: full weeks from Monday to Sunday. */
export function getCalendarInterval(month: string) {
	const firstOfMonth = parseISO(`${month}-01`);
	return {
		start: startOfWeek(firstOfMonth, { weekStartsOn: 1 }),
		end: endOfWeek(endOfMonth(firstOfMonth), { weekStartsOn: 1 }),
	};
}

// The controller loads the trackings of the month only, so a day of a neighbor month shows none.
export function getCalendarDays({ absences, absenceTypes, holidays, month, timeTrackings }: {
	absences: Absence[] | undefined;
	absenceTypes: AbsenceType[] | undefined;
	holidays: Holiday[];
	month: string;
	timeTrackings: TimeTrackingExtendedType[] | undefined;
}): CalendarDay[] {
	const firstOfMonth = startOfMonth(parseISO(`${month}-01`));
	const today = new Date();

	return eachDayOfInterval(getCalendarInterval(month)).map((date) => {
		const isInMonth = isSameMonth(date, firstOfMonth);
		return {
			date,
			isInMonth,
			isToday: isSameDay(date, today),
			isWeekend: isWeekend(date),
			timeTrackings: isInMonth
				? (timeTrackings ?? []).filter((timeTracking) => isSameDay(new Date(timeTracking.date), date))
				: [],
			holiday: holidays.find((holiday) => isSameDay(Date.parse(holiday.date), date)),
			absences: getAbsencesOnDay({ absences, absenceTypes, date }),
		};
	});
}

export type CalendarAbsenceActions = {
	getRules: (absence: Absence, absenceTypeName: string) => { isEditable: boolean; isDeletable: boolean };
	openEdit: (absenceId: string) => void;
	openDelete: (absenceId: string) => void;
	download: (absence: Absence, absenceTypeName: string) => void;
};

export type CalendarActions = {
	absenceActions?: CalendarAbsenceActions;
	canAddTrackingOn: (date: Date) => boolean;
	/** A user with a staff member can book an absence on each day, so each day shows an enabled "+". */
	canAddAbsence: boolean;
	showDisabledAddTracking: boolean;
	openCreate: (date: Date) => void;
	openUpdate: (timeTrackingId: string) => void;
	openDelete: (timeTrackingId: string) => void;
	selectedTimeTrackingId: string | null;
	setSelectedTimeTrackingId: (timeTrackingId: string | null) => void;
};

export const weekdayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
